// Right Chat - estado da tela do Kanban proprio.
//
// De proposito SEM Vuex e SEM Pinia: registrar um modulo obrigaria a sobrescrever o
// indice de store do upstream (arquivo que o Chatwoot mexe a cada versao) e nada aqui
// precisa ser global fora desta tela. O estado e de modulo, entao a tela e os dialogos
// compartilham a mesma instancia sem prop drilling.
import { ref, computed } from 'vue';
import RightKanbanAPI from '../api/rightKanban';
import {
  byPosition,
  applyOptimisticMove,
  errorMessage,
} from '../rightKanban/ordering';

export { errorMessage };

const boards = ref([]);
const currentBoardId = ref(null);
const board = ref(null);
const tasks = ref([]);

const loadingBoards = ref(false);
const loadingBoard = ref(false);
const saving = ref(false);
const error = ref('');

export function useRightKanban() {
  const steps = computed(() => [...(board.value?.steps || [])].sort(byPosition));

  const columns = computed(() =>
    steps.value.map(step => ({
      step,
      tasks: tasks.value
        .filter(task => task.board_step_id === step.id)
        .sort(byPosition),
    }))
  );

  const hasBoards = computed(() => boards.value.length > 0);
  const hasSteps = computed(() => steps.value.length > 0);
  const isEmptyBoard = computed(() => hasSteps.value && tasks.value.length === 0);
  const totalValue = computed(() =>
    tasks.value.reduce((sum, task) => sum + (Number(task.value) || 0), 0)
  );

  const clearError = () => {
    error.value = '';
  };

  const fetchBoards = async () => {
    loadingBoards.value = true;
    clearError();
    try {
      const { data } = await RightKanbanAPI.getBoards();
      boards.value = data?.boards || [];
      if (!boards.value.some(item => item.id === currentBoardId.value)) {
        currentBoardId.value = boards.value[0]?.id || null;
      }
    } catch (err) {
      error.value = errorMessage(err);
      boards.value = [];
    } finally {
      loadingBoards.value = false;
    }
  };

  const fetchBoard = async (boardId = currentBoardId.value) => {
    if (!boardId) {
      board.value = null;
      tasks.value = [];
      return;
    }
    loadingBoard.value = true;
    clearError();
    try {
      // Duas chamadas de proposito: `boards/:id` traz cor e posicao das etapas, que o
      // endpoint de steps NAO pode carregar (ele e contrato com o `agents`).
      const [boardResponse, tasksResponse] = await Promise.all([
        RightKanbanAPI.getBoard(boardId),
        RightKanbanAPI.getTasks({ board_id: boardId }),
      ]);
      board.value = boardResponse.data;
      currentBoardId.value = boardResponse.data?.id || boardId;
      tasks.value = tasksResponse.data?.tasks || [];
    } catch (err) {
      error.value = errorMessage(err);
      board.value = null;
      tasks.value = [];
    } finally {
      loadingBoard.value = false;
    }
  };

  const selectBoard = async boardId => {
    currentBoardId.value = boardId ? Number(boardId) : null;
    await fetchBoard(currentBoardId.value);
  };

  // Move otimista: a coluna reordena na hora e volta ao estado anterior se o servidor
  // recusar. Sem o rollback, um 422 deixaria a tela mentindo ate o proximo F5.
  const moveTask = async ({ taskId, toStepId, insertBeforeTaskId = null }) => {
    const task = tasks.value.find(item => item.id === taskId);
    if (!task) return;
    if (task.board_step_id === toStepId && !insertBeforeTaskId) return;

    const snapshot = tasks.value.map(item => ({ ...item }));
    tasks.value = applyOptimisticMove(tasks.value, {
      taskId,
      toStepId,
      insertBeforeTaskId,
    });

    clearError();
    try {
      await RightKanbanAPI.moveTask(taskId, {
        boardStepId: toStepId,
        insertBeforeTaskId,
      });
      // O servidor reordena os irmaos da coluna inteira; so ele sabe o resultado final.
      const { data } = await RightKanbanAPI.getTasks({
        board_id: currentBoardId.value,
      });
      tasks.value = data?.tasks || tasks.value;
    } catch (err) {
      tasks.value = snapshot;
      error.value = errorMessage(err);
    }
  };

  const runSaving = async fn => {
    saving.value = true;
    clearError();
    try {
      return await fn();
    } catch (err) {
      error.value = errorMessage(err);
      return null;
    } finally {
      saving.value = false;
    }
  };

  const createBoard = name =>
    runSaving(async () => {
      const { data } = await RightKanbanAPI.createBoard(name);
      await fetchBoards();
      await selectBoard(data.id);
      return data;
    });

  const renameBoard = (boardId, name) =>
    runSaving(async () => {
      const { data } = await RightKanbanAPI.updateBoard(boardId, name);
      await fetchBoards();
      if (board.value?.id === boardId) board.value.name = data.name;
      return data;
    });

  const deleteBoard = boardId =>
    runSaving(async () => {
      await RightKanbanAPI.deleteBoard(boardId);
      currentBoardId.value = null;
      await fetchBoards();
      await fetchBoard(currentBoardId.value);
      return true;
    });

  const createStep = (boardId, step) =>
    runSaving(async () => {
      const { data } = await RightKanbanAPI.createStep(boardId, step);
      await fetchBoard(boardId);
      return data;
    });

  const updateStep = (boardId, stepId, step) =>
    runSaving(async () => {
      const { data } = await RightKanbanAPI.updateStep(boardId, stepId, step);
      await fetchBoard(boardId);
      return data;
    });

  // Etapa com card devolve 409 (dependent: :restrict_with_error no modelo). A mensagem
  // do servidor ja explica o que fazer, entao ela sobe crua para o banner.
  const deleteStep = (boardId, stepId) =>
    runSaving(async () => {
      await RightKanbanAPI.deleteStep(boardId, stepId);
      await fetchBoard(boardId);
      return true;
    });

  const updateTask = (taskId, payload) =>
    runSaving(async () => {
      const { data } = await RightKanbanAPI.updateTask(taskId, payload);
      const current = tasks.value.find(item => item.id === taskId);
      if (current) Object.assign(current, data);
      return data;
    });

  return {
    boards,
    board,
    tasks,
    currentBoardId,
    steps,
    columns,
    hasBoards,
    hasSteps,
    isEmptyBoard,
    totalValue,
    loadingBoards,
    loadingBoard,
    saving,
    error,
    clearError,
    fetchBoards,
    fetchBoard,
    selectBoard,
    moveTask,
    createBoard,
    renameBoard,
    deleteBoard,
    createStep,
    updateStep,
    deleteStep,
    updateTask,
  };
}

export default useRightKanban;
